// The Recruiter screen card (Step 8, DS §1.12, DC-220…DC-222), mounted at the top of the screen round page above the
// scheduling section. "Recruiter owns screen content and outcome." · "Recruiter records outcome." — the screen's
// holder edits *First contact · Follow-up* and *Screen content* (saved with `screen.save_form`) and records the
// outcome *Advance · Hold · Pass · Withdrawn* (`screen.record_outcome`): Advance · Hold · Pass wait for *Completed*
// ("Screen Requested -> Scheduled -> Completed -> Advance/Hold/Pass/Withdrawn."), Withdrawn needs its reason, stage and
// date ("Withdrawal requires reason/stage/date."). "HM sees authorized results.": the Hiring Manager reads the outcome
// and its date only, once there is one; the HR Business Partner reads the values; an Interviewer, and anyone the
// screen's read refuses, sees nothing. Compensation is never on the card.

import { useEffect, useRef, useState } from "react";
import type { ScreenRecordOutcomeInput } from "@/api/tools";
import { candidateOpen, screenOpenResult, screenRecordOutcome, screenSaveForm } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { G6_COLUMN_LABELS, RULE_LABELS, RecruiterScreen, SCREEN_LABELS, type ScreenDates, type ScreenOutcomeDraft, type ScreenSeat } from "@/mc";
import { useSettledRead } from "@/views/shared/settledRead";
import { useHoldReasonChoices, usePassReasonChoices, useWithdrawalReasonChoices } from "@/views/candidate/options";
import { dayOf, instantOf, screenStatusOf } from "@/views/pipeline/pipeline";
import { pipelineRefusal } from "@/views/pipeline/refusals";
import { useSession } from "@/session/useSession";
import { CardReadSlot, cardReadState } from "./CardRead";

/** The stage a screen withdrawal is recorded at: the screen (the server checks it against the application). */
const STAGES = [{ value: "screen", label: G6_COLUMN_LABELS.recruiterScreen }] as const;

/** The card's final height (RecruiterScreen draws its own loading state at it). */
const CARD_HEIGHT = 420;

/** How long typing in *Screen content* waits before it is saved. */
const SAVE_AFTER_MS = 800;

export function RecruiterScreenCard({ rid, aid }: { rid: string; aid: string }) {
  const { can } = useSession();
  const readable = can("screen.open_result");
  const holdReasons = useHoldReasonChoices(readable);
  const withdrawalReasons = useWithdrawalReasonChoices(readable);
  const passReasons = usePassReasonChoices(readable);
  const app = useSettledRead(["application", aid, rid], () => candidateOpen({ requisition_id: rid, application_id: aid }), { enabled: readable });
  const res = useSettledRead(["screen", aid, "card"], () => screenOpenResult({ requisition_id: rid, application_id: aid }), { enabled: readable, retry: false });
  const inval = [["screen", aid], ["application", aid], ["candidates", rid], ["pipeline"], ["round", rid, aid], ["today"]] as const;
  const save = useWrite((input: Parameters<typeof screenSaveForm>[0], key) => screenSaveForm(input, key), inval);
  const record = useWrite((input: ScreenRecordOutcomeInput, key) => screenRecordOutcome(input, key), inval);
  const [content, setContent] = useState<string | null>(null);
  const [dates, setDates] = useState<ScreenDates | null>(null);
  const [value, setValue] = useState<ScreenOutcomeDraft["value"]>(undefined);
  const [refused, setRefused] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Typed content waiting for its save is sent, not dropped, when the card unmounts (the round page reloads its
  // sections after a date is saved, which remounts the card inside the wait).
  const unsaved = useRef<string | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (unsaved.current !== null) void screenSaveForm({ requisition_id: rid, application_id: aid, content: unsaved.current }).catch(() => undefined);
  }, [rid, aid]);

  if (!readable) return null;
  const state = cardReadState(res);
  if (state !== "ready" || !res.data) {
    return (
      <CardReadSlot
        state={state} title={SCREEN_LABELS.recruiterScreen} icon="phone" height={CARD_HEIGHT} error={res.error}
        onRetry={() => void res.refetch()} retrying={res.isFetching} 
      />
    );
  }
  const out = res.data;
  const status = screenStatusOf(out);
  if (!status) return null;
  const seat: ScreenSeat = out.projection === "holder" ? "recruiter" : out.projection === "hm" ? "hm" : "coordinator";
  const form = out.form as { content?: string | null; first_contact_at?: string | null; follow_up_at?: string | null };
  const shownDates: ScreenDates = dates ?? {
    firstContact: dayOf(form.first_contact_at) || undefined,
    screen: dayOf(out.scheduled_at) || undefined,
    followUp: dayOf(form.follow_up_at) || undefined,
  };
  const saves = Boolean(out.can["save_form"]);
  const fail = (error: unknown) => setRefused(pipelineRefusal(error) ?? RULE_LABELS.failedSyncRetryable);
  const full = app.data && "candidate_no" in app.data ? app.data : null;
  const shownName = full && "display_name" in full.card ? (full.card.display_name ?? "") : "";

  const onDate = (key: keyof ScreenDates, day: string) => {
    const next = { ...shownDates, [key]: day || undefined };
    setDates(next);
    if (!saves || key === "screen") return; // the screen's own date is the scheduled event's (B5's section)
    setRefused(null);
    save.mutateAsync({
      requisition_id: rid,
      application_id: aid,
      ...(key === "firstContact" ? { first_contact_at: instantOf(day) } : { follow_up_at: instantOf(day) }),
    }).catch(fail);
  };
  const onContent = (text: string) => {
    setContent(text);
    if (!saves) return;
    if (timer.current) clearTimeout(timer.current);
    unsaved.current = text;
    timer.current = setTimeout(() => {
      timer.current = null;
      unsaved.current = null;
      setRefused(null);
      save.mutateAsync({ requisition_id: rid, application_id: aid, content: text }).catch(fail);
    }, SAVE_AFTER_MS);
  };
  const onRecord = (draft: ScreenOutcomeDraft) => {
    if (!draft.value) return;
    setRefused(null);
    const input: ScreenRecordOutcomeInput = { requisition_id: rid, application_id: aid, outcome: draft.value };
    if (draft.value === "hold") input.hold_reason = draft.reason ?? null;
    if (draft.value === "pass") input.reason_code = (draft.reason ?? null) as NonNullable<ScreenRecordOutcomeInput["reason_code"]> | null;
    if (draft.value === "withdrawn") {
      input.withdrawal = {
        reason_code: draft.reason as NonNullable<ScreenRecordOutcomeInput["withdrawal"]>["reason_code"],
        stage: draft.stage ?? "screen",
        effective_at: instantOf(draft.date ?? "") ?? "",
      };
    }
    record.mutateAsync(input).catch(fail);
  };

  return (
    <RecruiterScreen
      status={status}
      seat={seat}
      candidate={{ name: shownName, id: full?.candidate_no ?? "", hueKey: full?.card.token }}
      requisition={rid}
      dates={shownDates}
      onDate={onDate}
      content={content ?? form.content ?? ""}
      onContent={onContent}
      reasons={value === "pass" ? passReasons : holdReasons}
      withdrawalReasons={withdrawalReasons}
      stages={STAGES}
      onOutcome={(draft) => setValue(draft.value)}
      recorded={seat === "hm" && out.outcome_at ? { actor: "", at: out.outcome_at } : undefined}
      onRecord={onRecord}
      recording={record.isPending}
      loading={app.isLoading}
      refused={refused ? { reason: refused } : undefined}
      hs={{
        id: "pipe.screen.id", firstContact: "pipe.screen.first-contact", screen: "pipe.screen.date", followUp: "pipe.screen.follow-up",
        content: "pipe.screen.content", reason: "pipe.screen.reason", stage: "pipe.screen.stage", date: "pipe.screen.withdrawal-date",
        record: "pipe.screen.record",
        tiles: { advance: "pipe.screen.advance", hold: "pipe.screen.hold", pass: "pipe.screen.pass", withdrawn: "pipe.screen.withdrawn" },
      }}
    />
  );
}
